import {
  Badge,
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  KYDEventStatus,
  KYDAdminEventSummary,
} from "@common/interfaces/KYDEvent";
import { Link } from "react-router-dom";
import EventFilters from "./EventFilters";
import { useCurrentRefinements, useInfiniteHits } from "react-instantsearch";
import { useMemo } from "react";
import LoadingEmptyResults from "./LoadingEmptyResults";
import InfiniteScroll from "./InfiniteScroll";
import { FaHourglassHalf } from "react-icons/fa";
import { AlgoliaSortOrder } from "@common/interfaces/Algolia";

export const badgeForStatus = (status: KYDEventStatus) => {
  let bg = "gray";
  let name = "N/A";

  switch (status) {
    case "ARCHIVED":
      bg = "gray";
      name = "ARCHIVED";
      break;
    case "CANCELED":
      bg = "gray";
      name = "CANCELED";
      break;
    case "DRAFT":
      bg = "blue";
      name = "Draft";
      break;
    case "PENDING_APPROVAL":
      bg = "yellow";
      name = "Pending Approval";
      break;
    case "PENDING_ANNOUNCEMENT":
      bg = "yellow";
      name = "Unannounced";
      break;
    case "OPEN":
      bg = "green";
      name = "On Sale";
      break;
    case "OFFSALE":
      bg = "red";
      name = "Off Sale";
      break;
    default: {
    }
  }

  return (
    <Badge justifyItems={"center"} colorScheme={bg}>
      {name}
    </Badge>
  );
};

export const EventsTableMobile = ({
  onChangeSort,
  ...props
}: {
  onChangeSort: (sortOrder: AlgoliaSortOrder) => void;
}) => {
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: ["status"],
  });

  const noRefinements = currentRefinements.length === 0;
  const showStatus = noRefinements;

  const { items, showMore, isLastPage, results } =
    useInfiniteHits<KYDAdminEventSummary>({
      escapeHTML: false,
    });

  const hasMore = results.nbPages > 1;

  const currentStatus = useMemo(
    () =>
      currentRefinements
        .find((i) => i.attribute === "status")
        ?.refinements.find((r) => r.attribute === "status")?.value,
    [items]
  );

  return (
    <>
      <EventFilters onChangeSort={onChangeSort} />
      <Table {...props} fontSize={"small"} layout="fixed">
        <Thead>
          <Tr>
            <Th align="left" p={1} w="55%">
              Event
            </Th>
            <Th p={1} textAlign={"right"} width="45%">
              Sales
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {items.map((kydEvent) => (
            <Tr key={kydEvent.objectID}>
              <Td p={1} w="60%">
                <VStack alignItems={"flex-start"} w="full" spacing={0}>
                  <Box w="full">
                    <Link to={`/events/${kydEvent.id}`}>
                      <Text
                        fontWeight="medium"
                        noOfLines={1}
                        w="full"
                        fontSize={"medium"}
                      >
                        {kydEvent.name}
                      </Text>
                    </Link>
                  </Box>
                  <Text w="full" color="muted">
                    {kydEvent.display_start_at}
                  </Text>
                  <Text w="full" color="muted">
                    {kydEvent.venue?.name}
                  </Text>
                  {kydEvent.is_external ? (
                    <Badge mt={2} colorScheme="blue">
                      External
                    </Badge>
                  ) : null}
                  <Box pb={1}>
                    {showStatus ? badgeForStatus(kydEvent.status) : null}
                  </Box>
                </VStack>
              </Td>
              <Td p={1} width="45%">
                {kydEvent.realtime_metrics ? (
                  <VStack
                    w="full"
                    spacing={0}
                    justifyContent={"flex-end"}
                    alignItems="flex-end"
                  >
                    <Text>
                      {kydEvent.realtime_metrics
                        ? kydEvent.realtime_metrics.sales_display
                        : kydEvent.total_gmv_display}
                    </Text>
                    <HStack w="full" justifyContent={"flex-end"}>
                      <Text>
                        {kydEvent.realtime_metrics
                          ? kydEvent.realtime_metrics.tickets_display
                          : kydEvent.total_tickets_purchased_display}
                      </Text>
                      <Text>/</Text>
                      <Text>
                        {kydEvent.realtime_metrics
                          ? kydEvent.realtime_metrics.quantity_display
                          : kydEvent.total_tickets_purchased}
                      </Text>
                    </HStack>
                    <Tooltip
                      label={"Number of tickets requested on the waitlist"}
                    >
                      <Box
                        className="flex"
                        hidden={
                          !kydEvent.realtime_metrics ||
                          !kydEvent.realtime_metrics.waitlist_buy_tickets ||
                          kydEvent.realtime_metrics?.waitlist_buy_tickets === 0
                        }
                      >
                        <HStack opacity={0.5} flexShrink={0}>
                          <FaHourglassHalf fontSize="0.6rem" />
                          <Text fontSize={"x-small"}>
                            {
                              kydEvent.realtime_metrics
                                ?.waitlist_buy_tickets_display
                            }
                          </Text>
                        </HStack>
                      </Box>
                    </Tooltip>
                  </VStack>
                ) : (
                  <VStack
                    w="full"
                    spacing={0}
                    justifyContent={"flex-end"}
                    alignItems="flex-end"
                  >
                    <Text>{kydEvent.total_tickets_purchased}</Text>
                  </VStack>
                )}
              </Td>
            </Tr>
          ))}
          <LoadingEmptyResults />
        </Tbody>
      </Table>
      <InfiniteScroll
        hasMore={!isLastPage}
        loadMore={showMore}
        threshold={300} // Load more items when user is 300px from bottom
        preloadBatch={false} // Add this to preload the next batch
      />
    </>
  );
};
